import { forwardRef } from "react";

import { cx } from "@/utils";
import Clickable, { ClickableProps } from "@components/Clickable";

import styles from "./TextButton.module.css";

/**
 * TextButton
 * Used to put an inline button-ish element inside a text or to be used as a
 * lightly styled button.
 *
 * Uses ForwardRef for compatibility with bootstrap-react
 */

export type TextButtonProps = {
  icon?: IconComponent;
  component?: React.ElementType;
  block?: boolean;
  children?: React.ReactNode;
  color?: "primary" | "inherit";
  className?: string;
  iconPlacement?: "before" | "after";
} & ClickableProps;

const TextButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  TextButtonProps
>(function TextButton(
  {
    icon: Icon,
    children,
    block = false,
    color = "primary",
    component: Component = Clickable,
    className,
    iconPlacement = "before",
    ...props
  },
  ref
) {
  return (
    <Component
      className={cx(
        styles.root,
        {
          [styles.block]: block,
          [styles.colorInherit]: color === "inherit",
          [styles.colorPrimary]: color === "primary",
          [styles.iconAfter]: Icon && children && iconPlacement === "after",
          [styles.iconBefore]: Icon && children && iconPlacement === "before",
          [styles.iconSolo]: Icon && !children,
        },
        className
      )}
      {...props}
      ref={ref}
    >
      {Icon ? <Icon className={styles.icon} height="1em" /> : null}
      <span className={styles.content}>{children}</span>
    </Component>
  );
});

export default TextButton;
